import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { ShareIcon, XIcon } from "@heroicons/react/outline";

interface HTTPFormAddVariableProps {
  onAddVariable: (value: string) => void;
  availableVariables: {
    label: string;
    icon: any;
    options: {
      label: string;
      value: string;
    }[];
  }[];
}

const selectorStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    borderColor: "#ddd",
    "&:hover": {
      borderColor: "#ddd",
    },
    boxShadow: "none",
    "*": {
      boxShadow: "none !important",
    },
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    wordBreak: "break-all",
    backgroundColor: isSelected ? "#d54466" : isFocused ? "#d54466" : undefined,
    color: isSelected ? "#fff" : isFocused ? "#fff" : undefined,
    fontSize: "small",
    paddingLeft: "30px",
  }),
};

function HTTPFormAddVariable({
  onAddVariable,
  availableVariables,
}: HTTPFormAddVariableProps) {
  const { t } = useTranslation();
  const [showVariableSelect, setShowVariableSelect] = useState(false);

  const handleChangeVariable = useCallback(
    (option) => {
      if (option && option.value) {
        onAddVariable(option.value);
        setShowVariableSelect(false);
      }
    },
    [onAddVariable]
  );

  const formatGroupLabel = useCallback(
    (data) => (
      <div className="flex flex-nowrap py-1">
        <div className="rounded-full bg-hilos-light p-1">
          <data.icon
            aria-hidden="true"
            className={`h-4 w-4 text-hilos ${
              data.icon === ShareIcon ? "rotate-90" : ""
            }`}
          />
        </div>
        <div className="ml-2 self-center text-sm capitalize text-hilos">
          {t(data.label)}
        </div>
      </div>
    ),
    [t]
  );

  if (!showVariableSelect) {
    return (
      <div className="my-2 flex w-full justify-end">
        <button
          className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          type="button"
          onClick={(_) => setShowVariableSelect(true)}
        >
          {t("inbox:quick-replies.variables.add", "Add Variable")}
        </button>
      </div>
    );
  }

  return (
    <div className="my-2 w-full">
      <p className="my-1 text-xs text-gray-500">
        {t(
          "inbox-actions:builder.add-variable-description",
          "Insert contact or agent variables in your HTTP request body."
        )}
      </p>

      <div className="flex items-center">
        <div className="grow">
          <Select
            menuPosition="fixed"
            menuPlacement="auto"
            menuPortalTarget={document.body}
            options={availableVariables}
            formatGroupLabel={formatGroupLabel}
            onChange={handleChangeVariable}
            styles={selectorStyles}
          />
        </div>
        <div className="ml-2 grow-0">
          <button
            className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-hilos focus:ring-offset-2"
            type="button"
            onClick={() => setShowVariableSelect(false)}
          >
            <XIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default HTTPFormAddVariable;
